import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import './Widget.css';

interface AccumulationStats {
  strategy: {
    enabled: boolean;
    dailyCapitalLimit: number;
    orderSize: number;
    minDropPercentToTrade: number;
    maxGasGwei: number;
  };
  accumulation: {
    totalBWSAccumulated: number;
    totalCapitalDeployed: number;
    averagePurchasePrice: number;
    currentValue: number;
    unrealizedPnL: number;
  };
  trades: {
    totalTrades: number;
    successfulTrades: number;
    failedTrades: number;
    averageDropPercent: number;
  };
  lastTrade: number;
}

export const AccumulationStrategy: React.FC = () => {
  const [stats, setStats] = useState<AccumulationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.accumulation.getStats();
        setStats(response as unknown as AccumulationStats);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch accumulation data');
        console.error('Accumulation stats fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const formatUSD = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatBWS = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number): string => {
    return (value * 100).toFixed(2) + '%';
  };

  const getTimeSinceLastTrade = (timestamp: number): string => {
    if (timestamp === 0) return 'Never';
    const now = Date.now();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${minutes}m ago`;
    return `${minutes}m ago`;
  };

  if (loading) {
    return (
      <div className="widget accumulation-strategy">
        <h3>Strategic Accumulation</h3>
        <div className="widget-loading">Loading...</div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="widget accumulation-strategy">
        <h3>Strategic Accumulation</h3>
        <div className="widget-error">{error || 'No data available'}</div>
      </div>
    );
  }

  const successRate =
    stats.trades.totalTrades > 0
      ? ((stats.trades.successfulTrades / stats.trades.totalTrades) * 100).toFixed(1)
      : '0.0';

  const pnlClass = stats.accumulation.unrealizedPnL >= 0 ? 'positive' : 'negative';

  return (
    <div className="widget accumulation-strategy">
      <div className="widget-header">
        <h3>Strategic Accumulation</h3>
        <div className={`status-badge ${stats.strategy.enabled ? 'enabled' : 'disabled'}`}>
          {stats.strategy.enabled ? 'ACTIVE' : 'PAUSED'}
        </div>
      </div>

      <div className="strategy-config">
        <div className="config-row">
          <div className="config-item">
            <label>Daily Limit</label>
            <div className="value">{formatUSD(stats.strategy.dailyCapitalLimit)}</div>
          </div>
          <div className="config-item">
            <label>Order Size</label>
            <div className="value">{formatBWS(stats.strategy.orderSize)} BWS</div>
          </div>
        </div>
        <div className="config-row">
          <div className="config-item">
            <label>Min Drop to Trade</label>
            <div className="value">{formatPercent(stats.strategy.minDropPercentToTrade)}</div>
          </div>
          <div className="config-item">
            <label>Max Gas</label>
            <div className="value">{stats.strategy.maxGasGwei} Gwei</div>
          </div>
        </div>
      </div>

      <div className="accumulation-summary">
        <div className="summary-card primary">
          <label>Total BWS Accumulated</label>
          <div className="value highlight">{formatBWS(stats.accumulation.totalBWSAccumulated)}</div>
          <div className="subvalue">
            Avg Price: {formatUSD(stats.accumulation.averagePurchasePrice)}
          </div>
        </div>

        <div className="summary-card">
          <label>Capital Deployed</label>
          <div className="value">{formatUSD(stats.accumulation.totalCapitalDeployed)}</div>
          <div className="subvalue">
            Current Value: {formatUSD(stats.accumulation.currentValue)}
          </div>
        </div>

        <div className={`summary-card pnl ${pnlClass}`}>
          <label>Unrealized P&L</label>
          <div className="value">
            {stats.accumulation.unrealizedPnL >= 0 ? '+' : ''}
            {formatUSD(stats.accumulation.unrealizedPnL)}
          </div>
          <div className="subvalue">
            {stats.accumulation.totalCapitalDeployed > 0
              ? formatPercent(
                  stats.accumulation.unrealizedPnL / stats.accumulation.totalCapitalDeployed
                )
              : '0.00%'}
          </div>
        </div>
      </div>

      <div className="trade-stats">
        <h4>Trade Performance</h4>
        <div className="stats-grid">
          <div className="stat-item">
            <label>Total Trades</label>
            <div className="value">{stats.trades.totalTrades}</div>
          </div>
          <div className="stat-item">
            <label>Success Rate</label>
            <div className="value success">{successRate}%</div>
          </div>
          <div className="stat-item">
            <label>Avg Drop</label>
            <div className="value">{formatPercent(stats.trades.averageDropPercent)}</div>
          </div>
          <div className="stat-item">
            <label>Last Trade</label>
            <div className="value">{getTimeSinceLastTrade(stats.lastTrade)}</div>
          </div>
        </div>
      </div>

      <div className="strategy-info">
        <div className="info-badge">
          <span className="info-icon">📊</span>
          <span className="info-text">
            Month 1 Strategy: Single daily trade on 5%+ price drops with {formatUSD(stats.strategy.dailyCapitalLimit)} limit
          </span>
        </div>
      </div>
    </div>
  );
};
